// The composition root of the route tree: the only place that imports every feature. A feature
// adds its routes here, under the shell route that scopes it (src/platform/routes.tsx).
import type { QueryClient } from "@tanstack/react-query";
import { createRouter, type RouterHistory } from "@tanstack/react-router";

import { accountRoute } from "../features/account/routes";
import { achievementsRoute } from "../features/achievements/routes";
import { appearanceRoute } from "../features/appearance/routes";
import { acceptInvitationRoute, googleCallbackRoute, signInRoute } from "../features/auth/routes";
import {
  brandHomeRoute,
  brandListRoute,
  editBrandRoute,
  newBrandRoute,
  newProductRoute,
  productListRoute,
  productRoute,
} from "../features/brands/routes";
import {
  editorFromAssetRoute,
  editorProjectRoute,
  editorProjectsRoute,
} from "../features/editor/routes";
import { generateRoute, jobListRoute, jobRoute } from "../features/generation/routes";
import { teamHomeRoute } from "../features/home/routes";
import { libraryRoute } from "../features/library/routes";
import { actorsRoute } from "../features/actors/routes";
import { soundsRoute } from "../features/sounds/routes";
import { templatesRoute } from "../features/templates/routes";
import {
  studio2BrandRoute,
  studio2EditBrandRoute,
  studio2NewBrandRoute,
  studio2NewProductRoute,
  studio2ProductRoute,
} from "./pro-brands";
import { studio2EditorRoute } from "./pro-editor";
import { studio2LibraryFilesRoute } from "./pro-library";
import { studio2NewTeamRoute } from "./pro-new-team";
import { studio2SoundsRoute } from "./pro-sounds";
import {
  studio2AccountRoute,
  studio2AchievementsRoute,
  studio2AppearanceRoute,
  studio2TokensRoute,
} from "./pro-settings";
import { studio2SimpleRoute } from "./pro-simple";
import { studio2TemplatesRoute } from "./pro-templates";
import {
  staffAiTasksRoute,
  staffAuditRoute,
  staffGenerationsRoute,
  staffRoute,
  staffTeamRoute,
  staffTeamsRoute,
  staffTraceRoute,
  staffUsageRoute,
} from "../features/staff/routes";
import {
  createImageRoute,
  createSoundRoute,
  createTalkingRoute,
  createVideoRoute,
  home2Route,
  studio2ActorsRoute,
  studio2BrandsRoute,
  studio2GenerationsRoute,
  studio2LibraryRoute,
  studio2ProductsRoute,
  studio2SettingsRoute,
  studio2EntryRoute,
  studio2Route,
  studio2UnknownRoute,
} from "../features/studio2/routes";
import { membersRoute, newTeamRoute } from "../features/teams/routes";
import { mcpServerRoute } from "../features/mcp/routes";
import { oauthConsentRoute, oauthProblemRoute } from "../features/oauth/routes";
import { tokensRoute } from "../features/tokens/routes";
import { RouteErrorScreen } from "../platform/RouteErrorScreen";
import {
  brandRoute,
  indexRoute,
  rootRoute,
  signedInRoute,
  teamRoute,
  unknownRoute,
} from "../platform/routes";

export const routeTree = rootRoute.addChildren([
  signInRoute,
  googleCallbackRoute,
  acceptInvitationRoute,
  oauthConsentRoute,
  oauthProblemRoute,
  signedInRoute.addChildren([
    indexRoute,
    unknownRoute,
    newTeamRoute,
    staffRoute.addChildren([
      staffTeamsRoute,
      staffTeamRoute,
      staffUsageRoute,
      staffGenerationsRoute,
      staffTraceRoute,
      staffAiTasksRoute,
      staffAuditRoute,
    ]),
    teamRoute.addChildren([
      teamHomeRoute,
      brandListRoute,
      newBrandRoute,
      membersRoute,
      tokensRoute,
      mcpServerRoute,
      accountRoute,
      appearanceRoute,
      achievementsRoute,
      jobListRoute,
      jobRoute,
      studio2EntryRoute,
      brandRoute.addChildren([
        brandHomeRoute,
        editBrandRoute,
        productListRoute,
        newProductRoute,
        productRoute,
        generateRoute,
        libraryRoute,
        soundsRoute,
        actorsRoute,
        templatesRoute,
        editorProjectsRoute,
        editorProjectRoute,
        editorFromAssetRoute,
        studio2Route.addChildren([
          home2Route,
          studio2SimpleRoute,
          createImageRoute,
          createTalkingRoute,
          createVideoRoute,
          createSoundRoute,
          studio2ActorsRoute,
          studio2GenerationsRoute,
          studio2TemplatesRoute,
          studio2LibraryRoute,
          studio2LibraryFilesRoute,
          studio2ProductsRoute,
          studio2NewProductRoute,
          studio2ProductRoute,
          studio2BrandsRoute,
          studio2NewBrandRoute,
          studio2BrandRoute,
          studio2EditBrandRoute,
          studio2SettingsRoute,
          studio2AccountRoute,
          studio2AppearanceRoute,
          studio2AchievementsRoute,
          studio2TokensRoute,
          studio2SoundsRoute,
          studio2NewTeamRoute,
          studio2EditorRoute,
          studio2UnknownRoute,
        ]),
      ]),
    ]),
  ]),
]);

export interface AppRouterOptions {
  queryClient: QueryClient;
  /** Tests pass a memory history; the app uses the browser's. */
  history?: RouterHistory;
}

export function createAppRouter({ queryClient, history }: AppRouterOptions) {
  return createRouter({
    routeTree,
    context: { queryClient },
    ...(history === undefined ? {} : { history }),
    defaultPreload: "intent",
    defaultErrorComponent: RouteErrorScreen,
    // The query cache owns freshness; the router only asks it.
    defaultPreloadStaleTime: 0,
    // The shell's <main> scrolls, not the window: restore it, and reset it on a new page.
    scrollRestoration: true,
    scrollToTopSelectors: ["#main"],
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
